import { Link } from "react-router-dom";
import { Circle, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { FILMS, STATUSES } from "../data/films";

export default function InProduction() {
  const films = FILMS.filter((f) => f.status === STATUSES.IN_PRODUCTION);

  return (
    <main data-testid="in-production-page" className="bg-black pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-red)] mb-6 flex items-center gap-3">
          <Circle size={8} className="fill-[color:var(--dk-red)] text-[color:var(--dk-red)] animate-pulse" />
          Currently in Production
        </p>
        <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter">
          <MaskLines lines={["Cameras", "are rolling."]} />
        </h1>
        <Reveal delay={1} className="mt-10 max-w-xl">
          <p className="text-white/60 leading-relaxed">
            Three features currently moving through principal photography and
            post-production stages. Progress indicators updated regularly.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-28 space-y-24">
        {films.map((film, i) => (
          <Reveal key={film.slug} delay={i}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 border-t border-white/10 pt-16"
            data-testid={`in-prod-${film.slug}`}
          >
            <Link to={`/films/${film.slug}`}
              className="md:col-span-6 relative aspect-[4/3] overflow-hidden group bg-[color:var(--dk-muted)]"
            >
              <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-overline text-white bg-[color:var(--dk-red)] px-3 py-1">
                <Circle size={6} className="fill-white animate-pulse" /> In Production
              </span>
              <ArrowUpRight size={20} className="absolute top-4 right-4 text-white group-hover:text-[color:var(--dk-gold)] transition-colors" />
            </Link>

            <div className="md:col-span-6 flex flex-col justify-center">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-3">
                Project {String(i + 1).padStart(2, "0")} · {film.genre}
              </p>
              <h2 className="font-serif-display text-white text-4xl md:text-6xl leading-[1] tracking-tight">
                {film.title}
              </h2>
              <p className="mt-3 text-white/50 font-serif-display italic">
                {film.nepaliTitle} · {film.hindiTitle}
              </p>
              <p className="mt-6 text-white/70 leading-relaxed text-lg max-w-xl">
                {film.shortSynopsis}
              </p>

              <div className="mt-10">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-overline text-white/60">Production Progress</span>
                  <span className="font-serif-display text-3xl text-[color:var(--dk-gold)] tracking-tight">{film.progress}%</span>
                </div>
                <div className="h-1 bg-white/10 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[color:var(--dk-red)] to-[color:var(--dk-gold)]"
                    style={{ width: `${film.progress}%` }}
                  />
                </div>
              </div>

              <Link to={`/films/${film.slug}`}
                data-testid={`in-prod-link-${film.slug}`}
                className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-overline text-white eyebrow-link w-fit">
                Project details <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        ))}
      </section>
    </main>
  );
}
